import get from "lodash/get";
import isObject from "lodash/isObject";
import { useState } from "react";

const STORE_KEYS = [
  "users.filter",
  "users.filterExpanded",
  "users.filterType",
  // NB: "taskManager.filter" is deliberately NOT persisted — TM filter
  // state lives in the URL (A2, variant 1: empty URL = empty filter).
  // Persisting it here made one tab's filters leak into every other tab.
  "taskManager.filterExpanded",
  "recruitingMetrics.filter",
];

export const fromStoreByKey = (key, path, defValue = null) => {
  const value = fromStore(key, {});
  return get(value, path, defValue);
};

export const useExpandedFilter = (storeKey, filterName) => {
  const [showItems, setShowItems] = useState(
    fromStoreByKey(`${storeKey}.filterExpanded`, filterName, false)
  );
  return [showItems, setShowItems];
};

export const useFilterType = (storeKey, filterName) => {
  const [filterType, setFilterType] = useState(
    fromStoreByKey(`${storeKey}.filterType`, filterName, false)
  );
  return [filterType, setFilterType];
};

// Reads a JSON-serialized value out of localStorage.
//   - missing key   → defValue (the caller's fallback shape)
//   - parsed object → { ...defValue, ...value } so callers can rely
//                     on the default's keys even if the persisted
//                     blob is partial
//   - parse error   → defValue (treat malformed JSON as missing)
//
// Note: previously this returned `undefined` for missing keys even
// when a default was supplied, which broke `users.redux.js` —
// `filter: fromStore('users.filter', { role: null, ... })` initialised
// to `undefined` instead of the documented filter shape, then the
// users saga fired on mount, called `buildFilterQuery(undefined)` and
// threw. Filed in TECH_DEBT.md.
export const fromStore = (key, defValue) => {
  try {
    const serializedState = localStorage.getItem(key);
    if (serializedState === null) return defValue;
    const value = JSON.parse(serializedState);
    if (!isObject(value)) return value;

    return { ...defValue, ...value };
  } catch (err) {
    return defValue;
  }
};

const select = (state, key) => JSON.stringify(get(state, key));

export const withLocalStorage = (store) => {
  const { getState } = store;
  const { localStorage } = window;

  // One-time cleanup: TM filters used to be persisted here and leaked
  // between tabs (A2c). Nothing reads the key anymore — drop the stale
  // blob so it can't resurface via a rollback build.
  try {
    localStorage.removeItem("taskManager.filter");
  } catch (e) {
    // storage unavailable — nothing to clean
  }

  const handler = () => {
    STORE_KEYS.forEach((key) => {
      const value = select(getState(), key);
      localStorage.setItem(key, value);
    });
  };

  store.subscribe(handler);

  return store;
};
